/* eslint-disable no-nested-ternary */
import React, { useEffect, useContext } from 'react';
import { Box, Divider, Grid } from '@mui/material';
import { DragDropContext, Droppable, Draggable, DropResult } from 'react-beautiful-dnd';
import { useFormContext } from 'react-hook-form';
import { BusinessEntity, IRecordField, IRoleAccess, TargetTenant } from 'ui-component/records/types';
import { RenderInput } from 'ui-component/RecordView/components';
import { IGetRecordTypes } from 'views/CreateRecord/types';
import { QUERY_GET_RECORD_TYPES } from 'graphql/queries/recordTypes';
import { useLazyQuery } from '@apollo/client';
import { NON_EDITABLE_FIELDS, getEditableField } from 'ui-component/records/utils/editableFields';
import { findFieldByFieldName, getDropdownOptionList } from 'ui-component/RecordView/utils';
import { pinWay } from '../utils';
import { ObjectProperty } from 'views/backoffice/CustomObjects/types';
import { ListValue } from 'views/backoffice/CustomLists/types';
import { IProblemCode, IRecordStatus } from 'ui-component/RecordView/types';
import { IUser } from 'views/backoffice/users/types';
import { PanelLoader } from 'ui-component/RecordView/components/PanelLoader';
import { PanelFormsContext } from 'contexts/panelFormsContext';

interface EditPanelProps {
    recordTypeId?: string;
    isCreate?: boolean;
    onChangeRecordType: (recordTypeId: string) => void;
    loadingFields?: boolean;
    fieldAccessList: IRoleAccess[];
    handleOpenViewObjectPanel: (field: IRecordField, fieldName: string, editable: boolean) => void;
    inMemoryObjectProperties: { [key: string]: (ObjectProperty & { value: string })[] };
    customListValues: Record<number, ListValue[]>;
    headersToRender: {
        [k: string]: any;
    };
    dropdownsData: {
        loading: boolean;
        problemCodeList: IProblemCode[];
        recordStatusList: IRecordStatus[];
        usersList: IUser[];
        targetTenantList: TargetTenant[];
        allBusinessEntities: BusinessEntity[];
    };
    inputRefs?: React.MutableRefObject<Record<string, HTMLDivElement | null>>;
    showFieldMenu?: boolean;
    rightHandPanelWidth?: number;
    isRecordViewer?: boolean;
    additionalFieldsFormattedNamesToHeaders: Record<string, Partial<IRecordField>>;
}

export interface EditPanelForm extends CreatePanelForm {
    id: number;
}

export interface CreatePanelForm {
    recordType: number;
    problemCodeId: number;
    recordNumber: string;
    recordDate: string;
    dueDate: string;
    statusId: number;
    poNumber: string;
    assignedTo: number;
    approvedBy: number;
    [key: string]: any;
}

const EditPanel = ({
    isCreate,
    recordTypeId,
    onChangeRecordType,
    loadingFields,
    fieldAccessList,
    handleOpenViewObjectPanel,
    inMemoryObjectProperties,
    customListValues,
    headersToRender,
    dropdownsData,
    inputRefs,
    showFieldMenu = true,
    rightHandPanelWidth,
    isRecordViewer,
    additionalFieldsFormattedNamesToHeaders
}: EditPanelProps) => {
    const {
        control,
        resetField,
        watch,
        formState: { errors },
        setValue,
        clearErrors
    } = useFormContext<CreatePanelForm | EditPanelForm>();
    const {
        setCustomFieldOrder: onReorderFields,
        enableDragAndDrop,
        pinnedFields,
        customFieldOrder,
        setSort
    } = useContext(PanelFormsContext);
    const {
        problemCodeList,
        recordStatusList,
        usersList,
        targetTenantList,
        allBusinessEntities,
        loading: dropdownsLoading
    } = dropdownsData;
    const [getRecordTypes, { data: recordTypeData, loading }] = useLazyQuery<IGetRecordTypes>(QUERY_GET_RECORD_TYPES);
    const recordTypeSelected = watch('recordType');
    const recordTypeList = React.useMemo(
        () => recordTypeData?.getRecordTypes.map((el) => ({ ...el, order: Number(el.id) })) || [],
        [recordTypeData]
    );

    useEffect(() => {
        if (isCreate && !recordTypeData) getRecordTypes();
        if (isCreate && recordTypeSelected && recordTypeList.length > 0) {
            onChangeRecordType(String(recordTypeSelected));
        }
    }, [onChangeRecordType, recordTypeSelected, isCreate, recordTypeData, recordTypeList.length, getRecordTypes, setValue]);

    if (!headersToRender || !Object.keys(headersToRender).length) return <></>;
    if (loading || loadingFields) return <PanelLoader />;

    const editableFields = Object.entries(headersToRender || {}).filter(
        ([key]) => !(NON_EDITABLE_FIELDS as ReadonlyArray<string>).includes(key)
    );

    let orderedFields = editableFields;
    if (enableDragAndDrop) {
        if (customFieldOrder.length > 0) {
            const reorderedFields: [string, any][] = [];
            customFieldOrder.forEach((fieldName) => {
                const field = editableFields.find(
                    ([key, value]) => key === fieldName.split('-')[0] && Number(value.id) === Number(fieldName.split('-')[1])
                );
                if (field) {
                    reorderedFields.push(field);
                }
            });
            editableFields.forEach(([key, value]) => {
                if (!customFieldOrder.includes(`${key}-${value.id}`)) {
                    reorderedFields.push([key, value]);
                }
            });
            orderedFields = reorderedFields;
        }
    }

    const renderField = ([key, value]: [string, any]) => {
        const isCustomList = value?.listType;
        const formattedKey =
            Object.entries(additionalFieldsFormattedNamesToHeaders).find(([, val]) => val.actualFieldName === key)?.[0] || key;
        const options = isCustomList
            ? customListValues[value.listType.id]
            : getDropdownOptionList(
                  key as any,
                  recordTypeList,
                  recordStatusList,
                  problemCodeList,
                  usersList,
                  targetTenantList,
                  allBusinessEntities
              );

        return (
            <Box
                key={formattedKey}
                ref={
                    inputRefs?.current
                        ? (el: HTMLDivElement | null) => {
                              if (inputRefs.current) {
                                  (inputRefs.current as unknown as Record<string, HTMLDivElement | null>)[key] = el;
                              }
                          }
                        : undefined
                }
            >
                <RenderInput
                    isObjectProperty={!!value.objectDefinition}
                    resetField={resetField}
                    name={formattedKey}
                    label={key}
                    control={control}
                    fieldData={value as IRecordField}
                    errors={errors}
                    options={options}
                    isCustomList={isCustomList}
                    disabled={!getEditableField(key, fieldAccessList) || !value?.gridEditable}
                    isRequired={value?.required && value?.gridEditable}
                    setValue={setValue}
                    handleOpenViewObjectPanel={handleOpenViewObjectPanel}
                    inMemoryObjectProperties={inMemoryObjectProperties}
                    isRecordViewer={isRecordViewer}
                    width={rightHandPanelWidth}
                    loadingListValues={dropdownsLoading}
                    clearErrors={clearErrors}
                    isDraggable={false}
                    isDragging={false}
                    showFieldMenu={showFieldMenu}
                    rightHandPanelWidth={rightHandPanelWidth}
                />
            </Box>
        );
    };

    const pinnedUpFieldEntries = pinnedFields
        .filter((fieldName) => fieldName.includes(pinWay.up))
        .map((fieldName) => findFieldByFieldName(fieldName.replace(pinWay.up, ''), orderedFields, true))
        .filter(Boolean) as [string, any][];

    const pinnedDownFieldEntries = pinnedFields
        .filter((fieldName) => fieldName.includes(pinWay.down))
        .map((fieldName) => findFieldByFieldName(fieldName.replace(pinWay.down, ''), orderedFields, true))
        .filter(Boolean) as [string, any][];

    const unpinnedFieldEntries = orderedFields.filter(([label, data]) => {
        const fieldId = `${label}::${data.id}`;
        return !pinnedFields.some(
            (pinnedField) =>
                pinnedField.includes(fieldId) || pinnedField.includes(fieldId + pinWay.up) || pinnedField.includes(fieldId + pinWay.down)
        );
    });

    const handleDragEnd = (result: DropResult) => {
        if (!result.destination || !onReorderFields) return;
        if (result.destination.index === result.source.index) return;
        setSort('none');

        const newUnpinnedOrder = unpinnedFieldEntries.map(([label, data], index) => {
            const fieldId = `${label}-${data.id}`;
            if (fieldId === result.draggableId) {
                return `${label}::${data.id}::${result.destination?.index}`;
            }

            if (index >= (result.destination?.index || 0)) {
                return `${label}::${data.id}::${index + 1}`;
            }

            return `${label}::${data.id}::${index}`;
        });

        const newCompleteOrder = [
            ...pinnedUpFieldEntries.map(([label, data]) => `${label}::${data.id}`),
            ...pinnedDownFieldEntries.map(([label, data]) => `${label}::${data.id}`),
            ...newUnpinnedOrder
        ];

        onReorderFields(newCompleteOrder);
    };

    if (!enableDragAndDrop) {
        return (
            <Grid
                container
                data-testid="edit-form"
                sx={{
                    height: '100%',
                    flexDirection: 'column',
                    position: 'relative'
                }}
            >
                {pinnedUpFieldEntries.length > 0 && (
                    <Grid
                        item
                        sx={{
                            pl: '0.8745rem',
                            pr: '0.5rem',
                            width: '100%',
                            maxWidth: '100%',
                            position: 'sticky',
                            top: 0,
                            backgroundColor: 'background.paper',
                            zIndex: 1
                        }}
                    >
                        {pinnedUpFieldEntries.map(renderField)}
                        <Divider sx={{ my: '0.5rem', width: '100%' }} />
                    </Grid>
                )}

                <Box
                    sx={{
                        width: '100%',
                        pl: '0.8745rem',
                        pr: '0.5rem',
                        flex: 1,
                        overflowY: 'auto'
                    }}
                >
                    {isCreate && (
                        <RenderInput
                            key="recordType"
                            resetField={resetField}
                            name="recordType"
                            control={control}
                            fieldData={{ dataType: 'single-select' } as unknown as IRecordField}
                            errors={errors}
                            options={recordTypeList}
                            isCustomList={false}
                            disabled={!!recordTypeId}
                            setValue={setValue}
                            width={rightHandPanelWidth}
                            label="Record Type"
                            showFieldMenu={showFieldMenu}
                            rightHandPanelWidth={rightHandPanelWidth}
                            isRecordViewer={isRecordViewer}
                        />
                    )}

                    {unpinnedFieldEntries.map(renderField)}
                </Box>

                {pinnedDownFieldEntries.length > 0 && (
                    <Grid
                        item
                        sx={{
                            pl: '0.8745rem',
                            pr: '0.5rem',
                            width: '100%',
                            maxWidth: '100%',
                            position: 'sticky',
                            bottom: 0,
                            backgroundColor: 'background.paper',
                            zIndex: 1
                        }}
                    >
                        <Divider sx={{ my: '0.5rem', width: '100%' }} />
                        {pinnedDownFieldEntries.map(renderField)}
                    </Grid>
                )}
            </Grid>
        );
    }

    return (
        <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="edit-panel-fields">
                {(provided) => (
                    <Grid
                        container
                        data-testid="edit-form"
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        sx={{
                            height: '100%',
                            flexDirection: 'column',
                            position: 'relative'
                        }}
                    >
                        {pinnedUpFieldEntries.length > 0 && (
                            <Grid
                                item
                                sx={{
                                    pl: '0.8745rem',
                                    pr: '0.5rem',
                                    width: '100%',
                                    maxWidth: '100%',
                                    position: 'sticky',
                                    top: 0,
                                    backgroundColor: 'background.paper',
                                    zIndex: 1
                                }}
                            >
                                {pinnedUpFieldEntries.map(renderField)}
                                <Divider sx={{ my: '0.5rem', width: '100%' }} />
                            </Grid>
                        )}
                        <Box
                            sx={{
                                width: '100%',
                                pl: '0.8745rem',
                                pr: '0.5rem',
                                flex: 1,
                                overflowY: 'auto'
                            }}
                        >
                            {isCreate && (
                                <RenderInput
                                    key="recordType"
                                    resetField={resetField}
                                    name="recordType"
                                    control={control}
                                    fieldData={{ dataType: 'single-select' } as unknown as IRecordField}
                                    errors={errors}
                                    options={recordTypeList}
                                    isCustomList={false}
                                    disabled={!!recordTypeId}
                                    setValue={setValue}
                                    width={rightHandPanelWidth}
                                    label="Record Type"
                                    isDraggable={false}
                                    isDragging={false}
                                    showFieldMenu={showFieldMenu}
                                    rightHandPanelWidth={rightHandPanelWidth}
                                    isRecordViewer={isRecordViewer}
                                />
                            )}

                            {unpinnedFieldEntries.map(([key, value], index) => (
                                <Draggable key={`${key}-${value.id}`} draggableId={`${key}-${value.id}`} index={index}>
                                    {(dragProvided, snapshot) => {
                                        const isCustomList = value?.listType;
                                        const formattedKey =
                                            Object.entries(additionalFieldsFormattedNamesToHeaders).find(
                                                ([, val]) => val.actualFieldName === key
                                            )?.[0] || key;
                                        const options = isCustomList
                                            ? customListValues[value.listType.id]
                                            : getDropdownOptionList(
                                                  key as any,
                                                  recordTypeList,
                                                  recordStatusList,
                                                  problemCodeList,
                                                  usersList,
                                                  targetTenantList,
                                                  allBusinessEntities
                                              );

                                        return (
                                            <div
                                                ref={dragProvided.innerRef}
                                                {...dragProvided.draggableProps}
                                                {...dragProvided.dragHandleProps}
                                                style={{
                                                    ...dragProvided.draggableProps.style,
                                                    opacity: snapshot.isDragging ? 0.8 : 1,
                                                    transform: snapshot.isDragging ? dragProvided.draggableProps.style?.transform : 'none'
                                                }}
                                            >
                                                <Box
                                                    ref={
                                                        inputRefs?.current
                                                            ? (el: HTMLDivElement | null) => {
                                                                  if (inputRefs.current) {
                                                                      (
                                                                          inputRefs.current as unknown as Record<
                                                                              string,
                                                                              HTMLDivElement | null
                                                                          >
                                                                      )[key] = el;
                                                                  }
                                                              }
                                                            : undefined
                                                    }
                                                >
                                                    <RenderInput
                                                        resetField={resetField}
                                                        name={formattedKey}
                                                        label={key}
                                                        control={control}
                                                        fieldData={value as IRecordField}
                                                        errors={errors}
                                                        options={options}
                                                        isCustomList={isCustomList}
                                                        disabled={!getEditableField(key, fieldAccessList) || !value?.gridEditable}
                                                        isRequired={value?.required && value?.gridEditable}
                                                        setValue={setValue}
                                                        handleOpenViewObjectPanel={handleOpenViewObjectPanel}
                                                        inMemoryObjectProperties={inMemoryObjectProperties}
                                                        width={rightHandPanelWidth}
                                                        loadingListValues={dropdownsLoading}
                                                        clearErrors={clearErrors}
                                                        isDraggable
                                                        isDragging={snapshot.isDragging}
                                                        showFieldMenu={showFieldMenu}
                                                        rightHandPanelWidth={rightHandPanelWidth}
                                                    />
                                                </Box>
                                            </div>
                                        );
                                    }}
                                </Draggable>
                            ))}
                            {provided.placeholder}
                        </Box>

                        {pinnedDownFieldEntries.length > 0 && (
                            <Grid
                                item
                                sx={{
                                    pl: '0.8745rem',
                                    pr: '0.5rem',
                                    width: '100%',
                                    maxWidth: '100%',
                                    position: 'sticky',
                                    bottom: 0,
                                    backgroundColor: 'background.paper',
                                    zIndex: 1
                                }}
                            >
                                <Divider sx={{ my: '0.5rem', width: '100%' }} />
                                {pinnedDownFieldEntries.map(renderField)}
                            </Grid>
                        )}
                    </Grid>
                )}
            </Droppable>
        </DragDropContext>
    );
};

export default EditPanel;
